import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const empty = { title: "", slug: "", excerpt: "", body: "", cover_image: "", published: true };

export default function AdminBlog() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const load = () => api.get("/admin/blog").then(({ data }) => setItems(data.items));
    useEffect(() => { load(); }, []);

    const startCreate = () => { setForm(empty); setEditing(null); setCreating(true); };
    const startEdit = (p) => { setForm({ ...p }); setEditing(p.slug); setCreating(false); };
    const cancel = () => { setEditing(null); setCreating(false); };

    const save = async (e) => {
        e.preventDefault();
        const payload = { ...form };
        try {
            if (creating) await api.post("/admin/blog", payload);
            else await api.put(`/admin/blog/${editing}`, payload);
            toast.success("Saved"); cancel(); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const remove = async (slug) => {
        if (!window.confirm(`Delete post "${slug}"?`)) return;
        try { await api.delete(`/admin/blog/${slug}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
    };

    return (
        <div data-testid="admin-blog">
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[hsl(var(--ink-soft))]">Journal</div>
                    <h1 className="mt-1 font-display text-4xl uppercase italic text-[hsl(var(--ink))]">Blog posts</h1>
                </div>
                <button onClick={startCreate} className="btn-primary">+ New post</button>
            </div>
            {(editing || creating) && (
                <form onSubmit={save} className="mt-6 grid gap-4 border-2 border-[hsl(var(--ink))] bg-card p-6 md:grid-cols-2">
                    {[["title", "Title"], ["slug", "Slug"], ["cover_image", "Cover URL"]].map(([k, l]) => (
                        <label key={k} className="text-sm">
                            <div className="font-heading text-xs uppercase tracking-wider">{l}</div>
                            <input required type="text" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} disabled={k === "slug" && !!editing} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm disabled:opacity-60" />
                        </label>
                    ))}
                    <label className="text-sm">
                        <div className="font-heading text-xs uppercase tracking-wider">Published</div>
                        <select value={form.published ? "1" : "0"} onChange={(e) => setForm({ ...form, published: e.target.value === "1" })} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm">
                            <option value="1">Yes</option>
                            <option value="0">No (draft)</option>
                        </select>
                    </label>
                    <label className="text-sm md:col-span-2">
                        <div className="font-heading text-xs uppercase tracking-wider">Excerpt</div>
                        <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm" />
                    </label>
                    <label className="text-sm md:col-span-2">
                        <div className="font-heading text-xs uppercase tracking-wider">Body (HTML)</div>
                        <textarea rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1 w-full border border-[hsl(var(--ink))] bg-white px-3 py-2 font-mono-brand text-sm" />
                    </label>
                    <div className="md:col-span-2 flex gap-3">
                        <button type="submit" className="btn-primary">Save</button>
                        <button type="button" onClick={cancel} className="btn-ghost">Cancel</button>
                    </div>
                </form>
            )}
            <div className="mt-8 space-y-3">
                {items.map((p) => (
                    <div key={p.slug} className="flex flex-wrap items-center justify-between gap-3 border-2 border-[hsl(var(--ink))] bg-card p-5">
                        <div>
                            <div className="font-heading text-base uppercase text-[hsl(var(--ink))]">{p.title}</div>
                            <div className="font-mono-brand text-xs text-[hsl(var(--ink-soft))]">/{p.slug} · {p.published ? "published" : "draft"} · {new Date(p.created_at).toLocaleDateString()}</div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => startEdit(p)} className="border border-[hsl(var(--ink))] px-3 py-1 font-heading text-xs uppercase">Edit</button>
                            <button onClick={() => remove(p.slug)} className="border border-[hsl(var(--danger))] px-3 py-1 font-heading text-xs uppercase text-[hsl(var(--danger))]">Delete</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
